import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Eraser,
  Trash2,
  Loader2,
  Palette,
  Undo2,
  MapPin,
  RotateCw,
} from "lucide-react";
import { useZoomPan, canvasPos, ZoomControls } from "@/components/drawing/ZoomPan";
import { useRuler, RulerButton, DimensionDialog, MeasureHint } from "@/components/drawing/Ruler";
import { VCheckTransferBar } from "@/components/drawing/VCheckTransferBar";
import { WhatsNewTour } from "@/components/whatsnew/WhatsNewTour";
import api, { fileUrl } from "@/lib/api";

/**
 * Photo-Annotator: lädt ein bestehendes Foto und legt es als Hintergrund auf
 * ein Canvas. Der Nutzer kann mit Stift drauf markieren (Pufferspeicher,
 * Inneneinheit, Außeneinheit, Leitungsführung) und das Ergebnis als neue
 * Skizze speichern – nutzt den bestehenden `/sketches`-Endpoint.
 *
 * Route: `/projects/:id/photos/:photoId/annotate`
 *
 * Das Original-Foto bleibt unverändert. Die annotierte Version landet im
 * Skizzen-Tab und kann separat referenziert werden.
 */

const COLORS = ["#00E575", "#F59E0B", "#EF4444", "#3B82F6", "#A78BFA", "#FFFFFF"];
const SIZES = [3, 5, 8, 12];

// Vordefinierte Marker für typische WP-Annotationen. Klick wechselt die
// aktive Farbe und schreibt automatisch das Label vor dem nächsten Strich.
const QUICK_LABELS = [
  { id: "puffer", label: "Pufferspeicher", color: "#3B82F6" },
  { id: "inneneinheit", label: "Inneneinheit", color: "#00E575" },
  { id: "aussen", label: "Außeneinheit", color: "#F59E0B" },
  { id: "leitung", label: "Leitungsführung", color: "#EF4444" },
];

const ANNOTATION_CATEGORIES = [
  "Heizraum",
  "Leitungsweg",
  "Außeneinheit",
  "Speicher",
  "Wohnraum",
  "Hausansicht",
  "Sonstiges",
];

export default function PhotoAnnotator() {
  const { id, photoId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const zp = useZoomPan();
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const historyRef = useRef([]); // stack of ImageData snapshots for undo
  const imageRef = useRef(null); // backing photo for re-render

  const [photoMeta, setPhotoMeta] = useState(null); // {storage_path, category, description}
  const [color, setColor] = useState("#00E575");
  const [size, setSize] = useState(5);
  const [isEraser, setIsEraser] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Heizraum");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingLabel, setPendingLabel] = useState(null); // string – next click drops this text
  const ruler = useRuler({ ctxRef, canvasRef, color, zoom: zp.zoom, onStart: () => pushSnapshot(), onCancel: () => historyRef.current.pop(), historyLen: () => historyRef.current.length });

  /* ---------- 1. Foto-Metadaten laden ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        const photo = (data.photos || []).find((p) => p.id === photoId);
        if (!photo) {
          toast.error("Foto nicht gefunden");
          navigate(`/projects/${id}`);
          return;
        }
        if (cancelled) return;
        setPhotoMeta(photo);
        setCategory(
          ANNOTATION_CATEGORIES.includes((photo.category || "").split(" – ")[0])
            ? (photo.category || "").split(" – ")[0]
            : "Sonstiges"
        );
        setTitle(`Markierung – ${photo.category || "Foto"}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Foto konnte nicht geladen werden");
        navigate(`/projects/${id}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, photoId, navigate]);

  /* ---------- 2. Bild aufs Canvas zeichnen, sobald Metadaten da sind ---------- */
  useEffect(() => {
    if (!photoMeta) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const url = fileUrl(photoMeta.storage_path);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Fit canvas inside the available container while preserving aspect
      const container = canvas.parentElement;
      const maxW = container.clientWidth;
      const maxH = container.clientHeight;
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
      const w = Math.floor(img.naturalWidth * ratio);
      const h = Math.floor(img.naturalHeight * ratio);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.drawImage(img, 0, 0, w, h);
      ctxRef.current = ctx;
      imageRef.current = { img, w, h };
      // Initial snapshot for undo baseline
      historyRef.current = [
        ctx.getImageData(0, 0, canvas.width, canvas.height),
      ];
      setLoading(false);
      // Gemerkte Lineal-Referenz dieses Fotos (letzte Markierung) übernehmen
      api.get(`/projects/${id}/measure-ref`, { params: { photo_id: photoId } }).then((r) => {
        if (r.data?.ref_mm_per_width) {
          ruler.loadRef(r.data.ref_mm_per_width);
          toast.info("Lineal-Referenz dieses Fotos übernommen.");
        }
      }).catch(() => {});
    };
    img.onerror = () => {
      toast.error("Bild konnte nicht geladen werden (Cross-Origin?)");
      setLoading(false);
    };
    img.src = url;
  }, [photoMeta]);

  /* ---------- 3. Drawing handlers ---------- */
  const getPos = (e) => canvasPos(canvasRef.current, e);

  const pushSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    historyRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    // keep history bounded
    if (historyRef.current.length > 25) historyRef.current.shift();
  }, []);

  const start = (e) => {
    if (!ctxRef.current) return;
    if (zp.gestureStart(e)) {
      // 2. Finger kam dazu → angefangenen Strich verwerfen
      if (drawing.current) { drawing.current = false; undo(true); }
      if (ruler.dragging() && !ruler.pending) ruler.cancel();
      return;
    }
    e.preventDefault();
    if (ruler.active) { ruler.down(getPos(e), e); return; }
    pushSnapshot();
    const p = getPos(e);
    // If a pending label is set → stamp it as text near the tap, then keep drawing.
    if (pendingLabel) {
      const ctx = ctxRef.current;
      ctx.save();
      ctx.font = "bold 16px Outfit, sans-serif";
      ctx.fillStyle = color;
      ctx.strokeStyle = "rgba(0,0,0,0.75)";
      ctx.lineWidth = 3;
      const text = pendingLabel;
      // Text with stroke for contrast on any background
      ctx.strokeText(text, p.x + 14, p.y - 6);
      ctx.fillText(text, p.x + 14, p.y - 6);
      // pin marker
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.85)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
      setPendingLabel(null);
    }
    drawing.current = true;
    lastPoint.current = p;
  };

  const move = (e) => {
    if (zp.gestureMove(e)) return;
    if (ruler.dragging()) { e.preventDefault(); ruler.update(getPos(e)); return; }
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const p = getPos(e);
    if (isEraser) {
      // Eraser: re-paint the original photo into a thick circular region around the path.
      // This restores the underlying image instead of painting solid color.
      const img = imageRef.current;
      if (img) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(p.x, p.y, (size * 3) / zp.zoom, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img.img, 0, 0, img.w, img.h);
        ctx.restore();
      }
    } else {
      ctx.strokeStyle = color;
      ctx.lineWidth = size / zp.zoom;
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    lastPoint.current = p;
  };

  const end = (e) => {
    zp.gestureEnd(e);
    if (ruler.dragging() && !ruler.pending) ruler.finish();
    drawing.current = false;
  };

  const undo = (silent = false) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    if (historyRef.current.length <= 1) {
      if (silent !== true) toast.info("Nichts zum Rückgängigmachen");
      return;
    }
    // Jeder Snapshot ist der Zustand VOR einer Aktion → zurückspielen
    ctx.putImageData(historyRef.current.pop(), 0, 0);
    ruler.trimTo(historyRef.current.length);
  };

  const clearAll = () => {
    ruler.trimTo(0);
    const ctx = ctxRef.current;
    const img = imageRef.current;
    if (!ctx || !img) return;
    pushSnapshot();
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    ctx.drawImage(img.img, 0, 0, img.w, img.h);
  };

  /* ---------- Rotation (90° im Uhrzeigersinn) ----------
   * Behält bestehende Markierungen bei: das gesamte Canvas (inkl. Strokes)
   * wird auf einen Off-Screen-Buffer übertragen, das Canvas neu dimensioniert,
   * und der Buffer rotiert zurückgezeichnet. Die `imageRef`-Baseline für den
   * Radierer wird parallel rotiert, damit der Eraser weiterhin das (gedrehte)
   * Originalfoto freilegt – nicht versehentlich eine Markierung.
   */
  const rotate = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const img = imageRef.current;
    if (!canvas || !ctx || !img) return;
    const dpr = window.devicePixelRatio || 1;

    // 1) Aktuelles Canvas-Bild in Buffer kopieren (inkl. Strokes)
    const buf = document.createElement("canvas");
    buf.width = canvas.width;
    buf.height = canvas.height;
    buf.getContext("2d").drawImage(canvas, 0, 0);

    // 2) Neue CSS-Dimensionen: Breite/Höhe getauscht
    const oldCssW = canvas.width / dpr;
    const oldCssH = canvas.height / dpr;
    const newCssW = oldCssH;
    const newCssH = oldCssW;
    canvas.width = newCssW * dpr;
    canvas.height = newCssH * dpr;
    canvas.style.width = `${newCssW}px`;
    canvas.style.height = `${newCssH}px`;

    // 3) Neuen Context konfigurieren + Buffer gedreht zeichnen
    const nctx = canvas.getContext("2d");
    nctx.scale(dpr, dpr);
    nctx.lineCap = "round";
    nctx.lineJoin = "round";
    nctx.save();
    nctx.translate(newCssW / 2, newCssH / 2);
    nctx.rotate(Math.PI / 2); // 90° CW
    nctx.drawImage(buf, -oldCssW / 2, -oldCssH / 2, oldCssW, oldCssH);
    nctx.restore();
    ctxRef.current = nctx;

    // 4) Eraser-Baseline (img) ebenfalls rotieren
    const off = document.createElement("canvas");
    off.width = img.h;
    off.height = img.w;
    const offCtx = off.getContext("2d");
    offCtx.translate(img.h / 2, img.w / 2);
    offCtx.rotate(Math.PI / 2);
    offCtx.drawImage(img.img, -img.w / 2, -img.h / 2, img.w, img.h);
    imageRef.current = { img: off, w: img.h, h: img.w };

    // 5) Undo-Historie zurücksetzen – alte Snapshots haben falsche Dimensionen
    historyRef.current = [
      nctx.getImageData(0, 0, canvas.width, canvas.height),
    ];
    toast.success("Bild gedreht (90°)");
  };

  /* ---------- 4. Save as new sketch ---------- */
  const save = async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    try {
      const blob = await new Promise((res) =>
        canvasRef.current.toBlob(res, "image/png", 0.92)
      );
      if (!blob) throw new Error("Canvas konnte nicht exportiert werden");
      const fd = new FormData();
      fd.append("file", blob, `annotation-${Date.now()}.png`);
      fd.append("category", category);
      fd.append("title", title || `Markierung ${category}`);
      fd.append("measurements", JSON.stringify(ruler.measurements));
      if (ruler.refPerWidth()) fd.append("ref_mm_per_width", String(ruler.refPerWidth()));
      fd.append("source_photo_id", photoId);
      await api.post(`/projects/${id}/sketches`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Markierung gespeichert (Skizzen)");
      navigate(`/projects/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const pickQuickLabel = (q) => {
    setColor(q.color);
    setIsEraser(false);
    setPendingLabel(q.label);
    toast.info(`Tippe ins Bild, um „${q.label}" zu setzen`);
  };

  return (
    <div
      className={`bg-[#09090B] flex flex-col ${zp.fullscreen ? "fixed inset-0 z-[80]" : "min-h-screen"}`}
      data-testid="photo-annotator-screen"
    >
      <header className={`bg-[#09090B]/95 border-b border-zinc-800 backdrop-blur-xl p-3 items-center gap-3 sticky top-0 z-10 ${zp.fullscreen ? "hidden" : "flex"}`}>
        <button
          data-testid="annotator-back-btn"
          onClick={() => navigate(`/projects/${id}`)}
          className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
        >
          <ArrowLeft size={20} />
        </button>
        <input
          data-testid="annotator-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel der Markierung…"
          className="flex-1 h-10 bg-transparent border-0 outline-none text-zinc-50 font-medium text-[15px] min-w-0"
        />
        <select
          data-testid="annotator-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 bg-zinc-800 border border-zinc-700 rounded-xl px-3 text-sm hidden sm:block"
        >
          {ANNOTATION_CATEGORIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <button
          data-testid="annotator-save-btn"
          onClick={save}
          disabled={saving || loading}
          className="h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-semibold inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <Save size={16} /> Speichern
            </>
          )}
        </button>
      </header>

      {/* Quick-Label Chip-Bar */}
      <div className={`bg-[#0d0d10] border-b border-zinc-800 px-3 py-2.5 overflow-x-auto ${zp.fullscreen ? "hidden" : ""}`}>
        <div className="flex gap-2 min-w-max">
          {QUICK_LABELS.map((q) => (
            <button
              key={q.id}
              data-testid={`annotator-quick-${q.id}`}
              onClick={() => pickQuickLabel(q)}
              className={`h-9 px-3 rounded-full border text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition active:scale-95 ${
                pendingLabel === q.label
                  ? "bg-zinc-100 text-[#09090B] border-zinc-100"
                  : "bg-zinc-900 text-zinc-200 border-zinc-700 hover:border-zinc-500"
              }`}
              style={pendingLabel !== q.label ? { borderColor: q.color + "55" } : {}}
            >
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ background: q.color }}
              />
              {q.label}
            </button>
          ))}
          {pendingLabel && (
            <button
              data-testid="annotator-cancel-label"
              onClick={() => setPendingLabel(null)}
              className="h-9 px-3 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-400 text-[12px] inline-flex items-center font-medium"
            >
              abbrechen
            </button>
          )}
        </div>
      </div>

      {/* Canvas-Bühne */}
      <div className={`flex-1 flex items-center justify-center overflow-hidden ${zp.fullscreen ? "p-0" : "p-3"}`}>
        <div
          className="relative bg-[#18181B] rounded-2xl border border-zinc-800 overflow-hidden flex items-center justify-center touch-none"
          style={{ width: "100%", height: zp.fullscreen ? "calc(100dvh - 76px)" : "calc(100vh - 240px)" }}
          onWheel={zp.onWheel}
          data-testid="annotator-stage"
        >
          {!loading && <ZoomControls zp={zp} testid="annotator-zoom" />}
          <MeasureHint ruler={ruler} testid="annotator-ruler-hint" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500 z-10">
              <Loader2 className="animate-spin mr-2" /> Bild wird geladen…
            </div>
          )}
          <canvas
            ref={canvasRef}
            data-testid="annotator-canvas"
            className="touch-none select-none"
            style={{ cursor: zp.panMode ? "grab" : pendingLabel ? "crosshair" : "default", ...zp.transformStyle }}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          {pendingLabel && !loading && (
            <div
              className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#00E575] text-[#09090B] text-[11.5px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 pointer-events-none shadow-lg"
            >
              <MapPin size={12} /> Tippe ins Bild für „{pendingLabel}"
            </div>
          )}
        </div>
      </div>

      <DimensionDialog ruler={ruler} />
      <WhatsNewTour page="draw" />
      <VCheckTransferBar ruler={ruler} projectId={id} />

      {/* Toolbar */}
      <div className="bg-[#09090B]/95 border-t border-zinc-800 backdrop-blur-xl p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Palette size={14} className="text-zinc-400" />
            {COLORS.map((c) => (
              <button
                key={c}
                data-testid={`annotator-color-${c}`}
                onClick={() => {
                  setColor(c);
                  setIsEraser(false);
                }}
                className={`h-8 w-8 rounded-full border-2 transition ${
                  color === c && !isEraser
                    ? "border-white scale-110"
                    : "border-zinc-700"
                }`}
                style={{ background: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {SIZES.map((s) => (
              <button
                key={s}
                data-testid={`annotator-size-${s}`}
                onClick={() => setSize(s)}
                className={`h-9 w-9 rounded-full flex items-center justify-center transition ${
                  size === s
                    ? "bg-[#00E575]/15 ring-1 ring-[#00E575]"
                    : "bg-zinc-800"
                }`}
              >
                <div
                  className="rounded-full bg-zinc-100"
                  style={{ width: s + 2, height: s + 2 }}
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <RulerButton ruler={ruler} testid="annotator-ruler-btn" />
            <button
              data-testid="annotator-undo-btn"
              onClick={() => undo()}
              className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
              title="Rückgängig"
            >
              <Undo2 size={18} />
            </button>
            <button
              data-testid="annotator-rotate-btn"
              onClick={rotate}
              className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
              title="Bild 90° drehen"
            >
              <RotateCw size={18} />
            </button>
            <button
              data-testid="annotator-eraser-btn"
              onClick={() => setIsEraser(!isEraser)}
              className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                isEraser ? "bg-[#00E575]/15 ring-1 ring-[#00E575]" : "bg-zinc-800"
              }`}
              title="Radierer"
            >
              <Eraser size={18} />
            </button>
            <button
              data-testid="annotator-clear-btn"
              onClick={clearAll}
              className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-red-900/30 hover:text-red-400 flex items-center justify-center transition"
              title="Alle Markierungen löschen"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
